import { useEffect, useState, useMemo } from 'react';
import { X, Search, Users, RefreshCcw, ArrowRight } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import useBoardStore from '../../store/boardStore';
import formatRelative, { formatAbsolute, parseDate } from '../../utils/formatRelative';

function initials(name) {
  return name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
}

function Avatar({ name, url, size = 28 }) {
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      title={name}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials(name)}
    </div>
  );
}

function Badge({ label, color }) {
  if (label == null || label === '') return <span className="text-text-secondary/60">—</span>;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold text-white truncate max-w-[160px] align-middle"
      style={{ backgroundColor: color || '#6b7280' }}
      title={label}
    >
      {label}
    </span>
  );
}

function ValueChangeBody({ details, statusOptions, users }) {
  const d = details;
  const type = d.column_type;
  const name = d.column_name || 'coluna';
  const fmt = (v) => {
    if (v == null) return null;
    if (type === 'person') return users.find((u) => u.id === v)?.name || `Usuário ${v}`;
    if (type === 'checkbox') return v ? 'Sim' : 'Não';
    if (typeof v === 'object') return v.label || v.url || JSON.stringify(v);
    return String(v);
  };
  const fromLabel = fmt(d.from);
  const toLabel = fmt(d.to);
  const colored = type === 'status' || type === 'select';
  const opts = colored ? (statusOptions?.[d.column_id] || []) : [];
  // M3c: resolve a cor pelo id quando o registro já traz {id,label} (valor
  // canônico gravado a partir de StatusCell.jsx); cai pro label cru em
  // registros antigos ou quando a etiqueta já foi excluída. M4b: o
  // snapshot de card.status.changed (events) usa a chave `option_id`, não
  // `id` (instantâneo histórico — cardValue.controller.js#snapshotStatusValue);
  // aceita as duas.
  const colorOf = (raw, label) => {
    const rawId = raw && typeof raw === 'object' ? (raw.option_id ?? raw.id) : null;
    if (rawId != null) {
      const byId = opts.find((o) => o.id === rawId);
      if (byId) return byId.color;
    }
    return opts.find((o) => o.label === label)?.color || null;
  };

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span>alterou <strong>{name}</strong>:</span>
      {colored ? <Badge label={fromLabel} color={colorOf(d.from, fromLabel)} /> : <em>{fromLabel ?? '—'}</em>}
      <ArrowRight className="w-3 h-3 text-text-secondary/60" />
      {colored ? <Badge label={toLabel} color={colorOf(d.to, toLabel)} /> : <em>{toLabel ?? '—'}</em>}
    </span>
  );
}

// M4b: nomes do catálogo novo (services/eventCatalog.js), lidos de `events`
// — não mais os nomes legados de activity_log.
function describe(a, users, statusOptions) {
  const d = a.details || {};
  switch (a.action) {
    // divider_created virou card.created com is_divider em data.
    case 'card.created': return d.is_divider ? <>criou uma divisória</> : <>criou o item</>;
    case 'card.renamed': return <>renomeou de <strong>{d.from}</strong> para <strong>{d.to}</strong></>;
    case 'card.status.changed':
    case 'card.assignee.changed':
    case 'card.value.updated':
    case 'card.value.cleared':
      return <ValueChangeBody details={d} statusOptions={statusOptions} users={users} />;
    // Sem preview de texto: events não guarda o conteúdo do comentário (ver
    // comment.routes.js). parent_id presente = resposta.
    case 'comment.created': {
      const attachSuffix = d.attachment_count ? <> (com {d.attachment_count} anexo{d.attachment_count > 1 ? 's' : ''})</> : '';
      return d.parent_id ? <>respondeu{attachSuffix}</> : <>comentou{attachSuffix}</>;
    }
    case 'attachment.uploaded':
      return <>anexou <strong>{d.original_name}</strong></>;
    case 'attachment.deleted':
      return <>removeu anexo <strong>{d.original_name}</strong></>;
    case 'card.moved':
      return <>moveu de <strong>{d.from_group_name ?? `grupo ${d.from_group_id}`}</strong> para <strong>{d.to_group_name ?? `grupo ${d.to_group_id}`}</strong></>;
    case 'card.archived':
      return <>arquivou o item</>;
    case 'card.restored':
      return <>restaurou o item</>;
    // Novo nesta fatia (M4b): antes invisível aqui — o INNER JOIN da rota
    // legada com `cards` apagava o histórico de um cartão junto com o
    // próprio cartão excluído.
    case 'card.deleted':
      return <>excluiu o item</>;
    // Novo nesta fatia (M4b): um GRUPO INTEIRO mudando de quadro nunca
    // existiu em activity_log (card_id era obrigatório).
    case 'group.moved':
      return <>moveu o grupo de <strong>{d.from_board_name ?? `quadro ${d.from_board_id}`}</strong> para <strong>{d.to_board_name ?? `quadro ${d.to_board_id}`}</strong></>;
    default:
      return <>ação: {a.action}</>;
  }
}

export default function BoardActivityModal({ onClose }) {
  const { currentBoard, users, statusOptions } = useBoardStore();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [personId, setPersonId] = useState(null);
  const [personOpen, setPersonOpen] = useState(false);

  // Carga inicial e "Recarregar": substitui a lista inteira (página 1).
  const load = async () => {
    if (!currentBoard) return;
    setLoading(true);
    try {
      const res = await authFetch(`/api/v1/boards/${currentBoard.id}/activity`, { full: true });
      setItems(res.data);
      setNextCursor(res.meta?.next_cursor ?? null);
      setHasMore(!!res.meta?.has_more);
    } finally { setLoading(false); }
  };

  // "Carregar mais": busca a próxima página pelo cursor e acrescenta no fim,
  // sem tocar no que já está na tela (sem pular o scroll).
  const loadMore = async () => {
    if (!currentBoard || !hasMore || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await authFetch(
        `/api/v1/boards/${currentBoard.id}/activity?cursor=${encodeURIComponent(nextCursor)}`,
        { full: true }
      );
      setItems((prev) => [...prev, ...res.data]);
      setNextCursor(res.meta?.next_cursor ?? null);
      setHasMore(!!res.meta?.has_more);
    } catch { /* ignore */ } finally { setLoadingMore(false); }
  };

  const [, setTick] = useState(0);

  useEffect(() => { load(); }, [currentBoard?.id]);

  // Poll (5s): busca só a primeira página e junta por id no topo da lista já
  // carregada — nunca substitui, senão as páginas trazidas por "Carregar
  // mais" desapareceriam a cada atualização periódica.
  useEffect(() => {
    if (!currentBoard?.id) return;
    const poll = async () => {
      try {
        const res = await authFetch(`/api/v1/boards/${currentBoard.id}/activity`, { full: true });
        const fresh = res.data;
        setItems((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          const onlyNew = fresh.filter((a) => !seen.has(a.id));
          return onlyNew.length ? [...onlyNew, ...prev] : prev;
        });
      } catch { /* ignore */ }
    };
    const timer = setInterval(poll, 5000);
    return () => clearInterval(timer);
  }, [currentBoard?.id]);

  useEffect(() => {
    const ticker = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(ticker);
  }, []);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (personId && a.user_id !== personId) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${a.user_name || ''} ${a.card_title || ''} ${a.action} ${JSON.stringify(a.details || {})}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, personId, search]);

  const groups = useMemo(() => {
    const out = [];
    const WINDOW = 5 * 60 * 1000;
    for (const a of filtered) {
      const last = out[out.length - 1];
      if (
        last &&
        last.user_id === a.user_id &&
        last.action === a.action &&
        Math.abs((parseDate(last.created_at)?.getTime() ?? 0) - (parseDate(a.created_at)?.getTime() ?? 0)) <= WINDOW
      ) {
        last.events.push(a);
        last.created_at = a.created_at;
      } else {
        out.push({
          user_id: a.user_id,
          user_name: a.user_name,
          user_avatar: a.user_avatar,
          action: a.action,
          created_at: a.created_at,
          events: [a],
        });
      }
    }
    return out;
  }, [filtered]);

  const uniqueUsers = useMemo(() => {
    const ids = new Set(items.map((a) => a.user_id).filter(Boolean));
    return users.filter((u) => ids.has(u.id));
  }, [items, users]);

  const personName = personId ? users.find((u) => u.id === personId)?.name : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[720px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-secondary">Registro</div>
            <h3 className="text-lg font-semibold text-text-primary">{currentBoard?.name}</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar"
              className="w-full bg-main border border-border rounded-md pl-7 pr-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setPersonOpen((v) => !v)}
              onBlur={() => setTimeout(() => setPersonOpen(false), 150)}
              className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
            >
              <Users className="w-4 h-4" />
              {personName || 'Pessoa'}
              {personId && <span className="text-[10px] bg-accent text-white rounded-full px-1.5">1</span>}
            </button>
            {personOpen && (
              <div className="absolute right-0 top-full mt-1 w-60 bg-card border border-border rounded-lg shadow-xl py-1 z-30 max-h-64 overflow-y-auto">
                <button
                  onMouseDown={(e) => { e.preventDefault(); setPersonId(null); setPersonOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 ${!personId ? 'text-accent' : 'text-text-primary'}`}
                >
                  Todos
                </button>
                {uniqueUsers.map((u) => (
                  <button
                    key={u.id}
                    onMouseDown={(e) => { e.preventDefault(); setPersonId(u.id); setPersonOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-white/5 ${personId === u.id ? 'text-accent' : 'text-text-primary'}`}
                  >
                    <Avatar name={u.name} url={u.avatar_url} size={20} />
                    <span className="truncate">{u.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={load}
            className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-white/5 rounded transition-colors"
            title="Recarregar"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>

        {hasMore && (
          <div className="px-5 py-1.5 text-[11px] text-text-secondary/60 border-b border-border/30">
            Busca e filtro consideram apenas os eventos já carregados.
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-sm text-text-secondary">Carregando...</div>
          ) : (
            <>
              {groups.length === 0 ? (
                <div className="text-center py-8 text-sm text-text-secondary">Nenhuma atividade encontrada.</div>
              ) : (
                <div className="space-y-4">
                  {groups.map((g, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-12 text-[11px] text-text-secondary pt-1 shrink-0" title={formatAbsolute(g.created_at)}>
                        {formatRelative(g.created_at)}
                      </div>
                      <Avatar name={g.user_name} url={g.user_avatar} />
                      <div className="flex-1 min-w-0 text-sm">
                        {g.events.length > 1 ? (
                          <>
                            <div className="text-text-primary">
                              <strong>{g.user_name || 'Usuário'}</strong>{' '}
                              alterou {g.events.length} {g.events.length === 1 ? 'item' : 'itens'}
                            </div>
                            <div className="mt-2 space-y-1.5 text-xs text-text-secondary">
                              {g.events.slice(0, 6).map((e) => (
                                <div key={e.id} className="flex items-center gap-2 flex-wrap">
                                  <span className="truncate max-w-[220px] text-text-primary">{e.card_title}</span>
                                  <span>·</span>
                                  <span>{describe(e, users, statusOptions)}</span>
                                </div>
                              ))}
                              {g.events.length > 6 && (
                                <div className="text-text-secondary/70">e mais {g.events.length - 6} eventos</div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-text-primary flex items-center gap-1.5 flex-wrap">
                            <strong>{g.user_name || 'Usuário'}</strong>
                            <span>{describe(g.events[0], users, statusOptions)}</span>
                            <span className="text-text-secondary">em</span>
                            <span className="text-accent">{g.events[0].card_title}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {hasMore && (
                <div className="pt-4 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
